// Trio fixo (canvas E05): a lista dos seus trios, criar trio, o trio (membros, convite e histórico) e o convite
// pelo link /t/CODIGO. O limite vem do plano: Grátis entra em 1 trio de amigo e não cria; Bronze 1; Prata até 3 com
// histórico; Ouro até 10. Os trios além do limite (depois de descer de plano) ficam guardados e fora da busca.
import { ligaNoAr } from '../comum/liga.ts';
import { useEffect, useState } from 'react';
import { ESCUDOS, MENSAGEM_NOME_TRIO, NOME_DE_TRIO_MAX, type PainelTrios, escudoDe, validarNomeDeTrio } from '@truco6/gamificacao';
import { cores } from '@truco6/ui';
import { type SituacaoConvite, type TrioAberto, criarTrio, entrarNoTrio, lerConviteDeTrio, lerTrio, lerTrios, sairDoTrio } from '../comum/api.ts';
import type { PerfilConta, Sessao } from '../comum/conta.ts';
import { medir } from '../comum/medicao.ts';
import { ir, voltar } from '../comum/rotas.ts';
import { Rosto } from '../comum/Rosto.tsx';
import { BotaoAmizade } from '../comum/BotaoAmizade.tsx';
import { type AmigoNaLista, convidarParaTrio, lerAmigos, lerConvitesDoTrio, lerPerfilPublico } from '../comum/amigos.ts';
import { avisarNaTela } from '../comum/aviso.tsx';
import { Carregando, Tela, Topo } from '../telas/comum.tsx';

const perfilCurto = (p: PerfilConta) => ({ apelido: p.apelido, avatar: p.avatar });
const linkDoConvite = (codigo: string) => `${location.origin}/t/${codigo}`;
const nomes = (ms: { apelido: string; usuario: string }[], eu: string) =>
  ms.map((m) => (m.usuario === eu ? 'Você' : m.apelido)).join(', ').replace(/, ([^,]*)$/, ' e $1');

// Escudo do trio: o formato de escudo com a cor e o emoji escolhidos.
export function Escudo({ id, tamanho = 48 }: { id: string; tamanho?: number }) {
  const e = escudoDe(id), cor = cores[e.cor];
  return (
    <span className="escudo" style={{ width: tamanho, height: tamanho * 1.15 }} aria-hidden="true">
      <svg viewBox="0 0 40 46" width={tamanho} height={tamanho * 1.15}>
        <path d="M20 2 37 8v14c0 11-7.5 18.5-17 22C10.5 40.5 3 33 3 22V8z" fill={cor} stroke={cores.cream050} strokeWidth="2.5" />
      </svg>
      <span style={{ fontSize: tamanho * 0.42 }}>{e.emoji}</span>
    </span>
  );
}

export function VerPlanos() {
  return <button className="btn2 dourado" onClick={() => ir({ tela: 'planos', plano: 'prata', ciclo: 'mensal' })}>Ver planos</button>;
}

export function TriosTela({ sessao }: { sessao: Sessao }) {
  const [p, setP] = useState<PainelTrios | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  useEffect(() => { lerTrios(sessao).then(setP, (e: Error) => setErro(e.message)); }, [sessao.id]);
  if (!p) return erro ? <Tela><Topo titulo="Trios fixos" voltar={voltar} /><p className="erro" role="alert">{erro}</p></Tela> : <Carregando />;
  const noLimite = p.trios.length >= p.limite;
  return (
    <Tela>
      <Topo titulo="Trios fixos" voltar={voltar} />
      <p className="sub">Seu plano: {p.cria ? `até ${p.limite} ${p.limite === 1 ? 'trio' : 'trios'}` : 'entra em 1 trio de amigo'}{p.historico ? ', com histórico' : ''}.</p>
      {p.trios.length === 0 && <p className="nota">Monte o seu trio e jogue a liga junto. Os pontos de trio contam quando os 3 estão na mesa.</p>}
      <ul className="lista-trios">
        {p.trios.map((t) => (
          <li key={t.id}>
            <button className="cartao linha-trio" onClick={() => ir({ tela: 'trio', id: t.id })}>
              <Escudo id={t.escudo} tamanho={44} />
              <span className="quem">
                <b>{t.nome}</b>
                <small>{nomes(t.membros, sessao.id)}</small>
              </span>
              <span className="etiquetas">
                {t.membros[0]?.usuario === sessao.id && <span className="etiqueta capitao">Capitão</span>}
                {!t.ativo && <span className="etiqueta guardado">Guardado</span>}
                {t.ativo && t.membros.length < 3 && <span className="etiqueta">{3 - t.membros.length === 1 ? 'Falta 1' : 'Faltam 2'}</span>}
              </span>
            </button>
          </li>
        ))}
      </ul>
      {p.trios.some((t) => !t.ativo) && (
        <p className="nota aviso-ranking">Trio guardado fica com o histórico, mas não entra na busca: seu plano permite {p.limite} {p.limite === 1 ? 'trio' : 'trios'}. Saia de algum ou volte ao plano.</p>
      )}
      {!p.cria ? (
        <div className="cadeado-tentos">
          <p className="cadeado">Criar trio fixo é dos planos Bronze, Prata e Ouro. No Grátis, você entra no trio de um amigo pelo convite.</p>
          <VerPlanos />
        </div>
      ) : noLimite ? (
        <div className="cadeado-tentos">
          <p className="cadeado">Seu plano permite {p.limite} {p.limite === 1 ? 'trio' : 'trios'}.</p>
          <VerPlanos />
        </div>
      ) : (
        <button className="btn1 largo" onClick={() => ir({ tela: 'novo-trio', convidar: null })}>CRIAR TRIO</button>
      )}
    </Tela>
  );
}

// convidar: "Criar trio novo com Léo" (chamar para o trio). O convite para ele sai assim que o trio for criado.
export function CriarTrio({ sessao, perfil, convidar = null }: { sessao: Sessao; perfil: PerfilConta; convidar?: string | null }) {
  const [convidado, setConvidado] = useState<string | null>(null);
  useEffect(() => {
    if (!convidar) return;
    let vivo = true;
    lerPerfilPublico(sessao, convidar).then((r) => { if (vivo && 'apelido' in r) setConvidado(r.apelido); }, () => {});
    return () => { vivo = false; };
  }, [convidar]);
  const [nome, setNome] = useState('');
  const [escudo, setEscudo] = useState<string>(ESCUDOS[0].id);
  const [erroNome, setErroNome] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [criando, setCriando] = useState(false);

  async function criar() {
    setErro(null);
    const v = validarNomeDeTrio(nome);
    if (v !== 'ok') { setErroNome(MENSAGEM_NOME_TRIO[v]); return; }
    setErroNome(null); setCriando(true);
    try {
      const t = await criarTrio(sessao, nome.trim(), escudo, perfilCurto(perfil));
      medir('trio_criado');
      if (convidar) {
        await convidarParaTrio(sessao, t.id, convidar).then(
          () => avisarNaTela(`Convite enviado para ${convidado ?? 'o jogador'}.`),
          (e: Error) => avisarNaTela(e.message),
        );
      }
      ir({ tela: 'trio', id: t.id }, true);
    } catch (e) {
      setErro((e as Error).message);
    } finally { setCriando(false); }
  }

  return (
    <Tela>
      <Topo titulo="Criar trio" voltar={voltar} />
      <div className="cartao criar-trio">
        <div className="criar-trio-topo">
          <Escudo id={escudo} tamanho={72} />
          <div className="campo">
            <label htmlFor="nome-trio">Nome do trio</label>
            <input id="nome-trio" value={nome} maxLength={NOME_DE_TRIO_MAX} autoComplete="off" placeholder="Os Parceiros" disabled={criando}
              aria-invalid={erroNome ? true : undefined} aria-describedby={erroNome ? 'erro-nome-trio' : undefined}
              onChange={(e) => { setNome(e.target.value); setErroNome(null); }} />
            {erroNome && <small id="erro-nome-trio" className="erro" role="alert">{erroNome}</small>}
          </div>
        </div>
        <div className="escudos" role="radiogroup" aria-label="Escudo">
          {ESCUDOS.map((e) => (
            <button key={e.id} role="radio" aria-checked={escudo === e.id} aria-label={'Escudo ' + e.emoji} className={escudo === e.id ? 'on' : undefined}
              disabled={criando} onClick={() => setEscudo(e.id)}>
              <Escudo id={e.id} tamanho={34} />
            </button>
          ))}
        </div>
      </div>
      <p className="nota">{convidar
        ? `Você é o capitão. ${convidado ?? 'O jogador'} recebe o convite assim que o trio for criado.`
        : 'Você é o capitão. Depois, chame 2 amigos pelo link do convite.'}</p>
      {erro && <p className="erro" role="alert">{erro}</p>}
      <button className="btn1 largo" disabled={criando} onClick={() => void criar()}>{criando ? 'CRIANDO...' : 'CRIAR TRIO'}</button>
    </Tela>
  );
}

export function TrioTela({ sessao, id }: { sessao: Sessao; id: string }) {
  const [t, setT] = useState<TrioAberto | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [copiado, setCopiado] = useState<string | null>(null);
  const [sair, setSair] = useState(false);
  useEffect(() => { lerTrio(sessao, id).then(setT, (e: Error) => setErro(e.message)); }, [sessao.id, id]);
  if (!t) return erro ? <Tela><Topo titulo="Trio fixo" voltar={voltar} /><p className="erro" role="alert">{erro}</p></Tela> : <Carregando />;
  const { trio, historico } = t, completo = trio.membros.length === 3, link = trio.convite ? linkDoConvite(trio.convite) : null;

  async function copiar() {
    if (!link) return;
    try { await navigator.clipboard.writeText(link); setCopiado('Link copiado!'); } catch { setCopiado('Copie o link: ' + link); }
    medir('share', { method: 'link', content_type: 'trio' });
    setTimeout(() => setCopiado(null), 2500);
  }

  return (
    <Tela>
      <Topo titulo="Trio fixo" voltar={voltar} />
      <div className="cartao trio-cab">
        <Escudo id={trio.escudo} tamanho={72} />
        <div><h2>{trio.nome}</h2><p className="nota">{completo ? 'Trio completo' : `${trio.membros.length} de 3 no trio`}</p></div>
      </div>
      <section aria-labelledby="h-membros" className="secao-trio">
        <h2 id="h-membros" className="rotulo">Membros · {trio.membros.length}</h2>
        <ul className="membros-trio">
          {trio.membros.map((m) => (
            <li key={m.usuario} className="cartao">
              <Rosto perfil={{ apelido: m.apelido, avatar: m.avatar }} usuario={m.usuario} cor="var(--nos-400)" tamanho={48} />
              <span className="quem"><b>{m.usuario === sessao.id ? 'Você' : m.apelido}</b>
                <small>No trio desde {new Date(m.entrouEm).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo', day: 'numeric', month: 'long' })}</small></span>
              <span className={'etiqueta' + (m.capitao ? ' capitao' : '')}>{m.capitao ? 'Capitão' : 'Membro'}</span>
              {m.usuario !== sessao.id && <BotaoAmizade usuario={m.usuario} apelido={m.apelido} compacto />}
            </li>
          ))}
        </ul>
      </section>
      {t.membro && link && !completo && (
        <section className="cartao convite-trio" aria-label="Convite do trio">
          <p><b>Chame {3 - trio.membros.length === 1 ? '1 amigo' : '2 amigos'}</b> para o trio.</p>
          {trio.membros.some((m) => m.usuario === sessao.id && m.capitao) && <AmigosParaOTrio sessao={sessao} trio={trio.id} membros={trio.membros.map((m) => m.usuario)} />}
          <p className="nota">Ou mande o link do convite:</p>
          <div className="dupla">
            <a className="btn2 whats" href={'https://wa.me/?text=' + encodeURIComponent(`Bora fechar o trio ${trio.nome} no Truco de 6? Entra aqui: ${link}`)}
              target="_blank" rel="noreferrer" onClick={() => medir('share', { method: 'whatsapp', content_type: 'trio' })}>Convidar pelo WhatsApp</a>
            <button className="btn2" onClick={() => void copiar()}>Copiar link</button>
          </div>
          {copiado && <p className="ok" role="status">{copiado}</p>}
        </section>
      )}
      {t.membro && !t.ativo && <p className="nota aviso-ranking">Este trio está guardado: seu plano não cobre mais um trio. Ele fica com o histórico, mas não entra na busca.</p>}
      {t.membro && completo && t.ativo && ligaNoAr() && (
        <button className="btn1 largo" onClick={() => ir({ tela: 'liga', trio: trio.id })}>JOGAR NA LIGA COM O TRIO</button>
      )}
      {t.membro && (historico ? (
        <section aria-labelledby="h-historico" className="secao-trio">
          <h2 id="h-historico" className="rotulo">Histórico</h2>
          <div className="numeros-trio">
            <p className="cartao"><b>{historico.partidas}</b><small>Partidas juntos</small></p>
            <p className="cartao"><b className="nos">{historico.aproveitamento}%</b><small>Vitórias</small></p>
            <p className="cartao"><b>{historico.pontosTemporada}</b><small>Pontos na temporada</small></p>
            <p className="cartao">{historico.posicao ? <b>{historico.posicao}º</b> : <b className="sem-valor">Ainda não</b>}<small>No ranking de trios</small></p>
          </div>
          {historico.ultimas.length > 0 && (
            <ul className="ultimas-trio" aria-label="Últimas partidas">
              {historico.ultimas.map((u) => (
                <li key={u.partida}>
                  <span>{new Date(u.em).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit' })}</span>
                  <b><span className="nos">{u.placar[0]}</span> x <span className="eles">{u.placar[1]}</span></b>
                  <span className={'etiqueta ' + (u.venceu ? 'ganhou' : 'perdeu')}>{u.venceu ? 'Ganhou' : 'Perdeu'}</span>
                  <small>+{u.pontos} pts</small>
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : (
        <div className="cadeado-tentos">
          <p className="cadeado">O histórico do trio (partidas, vitórias, pontos e posição) é dos planos Prata e Ouro.</p>
          <VerPlanos />
        </div>
      ))}
      {t.membro && (sair ? (
        <div className="cartao">
          <p>{trio.membros[0]?.usuario === sessao.id && trio.membros.length > 1 ? 'Você é o capitão: a capitania passa para quem entrou depois de você. ' : ''}Sair do trio mesmo assim?</p>
          <div className="dupla">
            <button className="btn2" onClick={() => setSair(false)}>Ficar</button>
            <button className="btn2 perigo" onClick={() => void sairDoTrio(sessao, trio.id).then(() => ir({ tela: 'trios' }, true), (e: Error) => setErro(e.message))}>Sair do trio</button>
          </div>
        </div>
      ) : <button className="link" onClick={() => setSair(true)}>Sair do trio</button>)}
      {erro && <p className="erro" role="alert">{erro}</p>}
    </Tela>
  );
}

// O convite do trio mostra os seus amigos primeiro (só para o capitão, que é quem chama), com o link por baixo.
function AmigosParaOTrio({ sessao, trio, membros }: { sessao: Sessao; trio: string; membros: string[] }) {
  const [amigos, setAmigos] = useState<AmigoNaLista[] | null>(null);
  const [enviados, setEnviados] = useState<ReadonlySet<string>>(new Set());
  const [ocupado, setOcupado] = useState<string | null>(null);
  useEffect(() => {
    let vivo = true;
    Promise.all([lerAmigos(sessao), lerConvitesDoTrio(sessao, trio).catch(() => ({ pendentes: [] }))]).then(([r, c]) => {
      if (!vivo) return;
      setEnviados(new Set(c.pendentes));
      setAmigos(r.amigos);
    }, () => { if (vivo) setAmigos([]); });
    return () => { vivo = false; };
  }, [sessao.id, trio]);
  const fora = amigos?.filter((a) => !membros.includes(a.usuario)) ?? [];
  if (!fora.length) return null;
  async function convidar(a: AmigoNaLista) {
    if (ocupado) return;
    setOcupado(a.usuario);
    try {
      await convidarParaTrio(sessao, trio, a.usuario);
      setEnviados((x) => new Set([...x, a.usuario]));
      avisarNaTela(`Convite enviado para ${a.apelido}.`);
    } catch (e) {
      avisarNaTela((e as Error).message);
    } finally { setOcupado(null); }
  }
  return (
    <div className="amigos-do-trio">
      <h3 className="rotulo">SEUS AMIGOS</h3>
      <ul className="lista-pessoas">
        {fora.map((a) => (
          <li key={a.usuario}>
            <Rosto perfil={{ apelido: a.apelido, avatar: a.avatar }} usuario={a.usuario} cor="var(--gold-600)" tamanho={40} />
            <span className="nome"><b>{a.apelido}</b>{a.trio && <small>{a.trio.nome}</small>}</span>
            {enviados.has(a.usuario)
              ? <span className="estado-trio convidado">Convite enviado</span>
              : <button className="btn2 dourado convidar-trio" disabled={ocupado === a.usuario} aria-label={`Convidar ${a.apelido} para o trio`}
                onClick={() => void convidar(a)}>Convidar</button>}
          </li>
        ))}
      </ul>
    </div>
  );
}

const TEXTO_DA_SITUACAO: Record<Exclude<SituacaoConvite, 'pode' | 'limite'>, string> = {
  ja_esta: 'Você já está neste trio.',
  cheio: 'Este trio já tem 3 jogadores.',
};

export function ConviteTrio({ sessao, perfil, codigo }: { sessao: Sessao; perfil: PerfilConta; codigo: string }) {
  const [c, setC] = useState<Awaited<ReturnType<typeof lerConviteDeTrio>> | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [entrando, setEntrando] = useState(false);
  useEffect(() => { lerConviteDeTrio(sessao, codigo).then(setC, (e: Error) => setErro(e.message)); }, [sessao.id, codigo]);
  if (!c) return erro ? <Tela><Topo titulo="Convite de trio" voltar={voltar} /><p className="erro" role="alert">{erro}</p></Tela> : <Carregando />;
  const { trio, situacao } = c;

  async function entrar() {
    setErro(null); setEntrando(true);
    try { const t = await entrarNoTrio(sessao, codigo, perfilCurto(perfil)); ir({ tela: 'trio', id: t.id }, true); }
    catch (e) { setErro((e as Error).message); } finally { setEntrando(false); }
  }

  return (
    <Tela>
      <Topo titulo="Convite de trio" voltar={voltar} />
      <div className="cartao trio-cab convite">
        <Escudo id={trio.escudo} tamanho={72} />
        <div>
          <h2>{trio.nome}</h2>
          <p className="nota">{nomes(trio.membros, sessao.id)} {trio.membros.length === 1 ? 'chamou' : 'chamaram'} você para o trio fixo.</p>
        </div>
      </div>
      <p className="sub">{ligaNoAr() ? 'No trio, vocês entram juntos na busca da Liga da Semana e somam pontos de trio quando os 3 estão na mesa.' : 'No trio, vocês somam pontos de trio no ranking quando os 3 estão na mesma mesa.'}</p>
      {situacao === 'pode' && <button className="btn1 largo" disabled={entrando} onClick={() => void entrar()}>ENTRAR NO TRIO</button>}
      {situacao === 'limite' && (
        <div className="cadeado-tentos">
          <p className="cadeado">{c.mensagem} Saia de um trio ou troque de plano para entrar neste.</p>
          <VerPlanos />
        </div>
      )}
      {(situacao === 'ja_esta' || situacao === 'cheio') && <p className="status" role="status">{TEXTO_DA_SITUACAO[situacao]}</p>}
      {situacao === 'ja_esta' && <button className="btn2 largo" onClick={() => ir({ tela: 'trio', id: trio.id }, true)}>Ver o trio</button>}
      {erro && <p className="erro" role="alert">{erro}</p>}
    </Tela>
  );
}
